<template>
  <AppLayout>
    <div class="p-4 md:p-6">

      <!-- Header (desktop only) -->
      <div class="hidden md:flex items-center justify-between mb-2">
        <div>
          <h1 class="text-2xl font-bold text-slate-800 dark:text-white">Account Requests</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
            {{ filteredContacts.length }} of {{ props.contacts.length }} request{{ props.contacts.length !== 1 ? 's' : '' }}
          </p>
        </div>
        <!-- Stats pills -->
        <div class="flex items-center gap-2">
          <span class="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400">
            <span class="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
            {{ pendingCount }} Pending
          </span>
          <span class="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400">
            <span class="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            {{ doneCount }} Done
          </span>
        </div>
      </div>

      <!-- ── MOBILE HEADER + STATS + SEARCH ── -->
      <div class="md:hidden -mx-4 -mt-4 mb-1">
        <!-- Rose gradient hero -->
        <div class="bg-gradient-to-br from-rose-600 via-rose-500 to-pink-500 px-4 pt-3 pb-6">
          <div class="flex items-center justify-between mb-3">
            <div>
              <h1 class="text-xl font-bold text-white">Account Requests</h1>
              <p class="text-rose-200 text-xs mt-0.5">
                {{ filteredContacts.length }} of {{ props.contacts.length }} request{{ props.contacts.length !== 1 ? 's' : '' }}
              </p>
            </div>
          </div>
          <!-- Stats row -->
          <div class="grid grid-cols-2 gap-2 mb-2">
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-amber-200 uppercase tracking-widest mb-1">Pending</p>
              <p class="text-sm font-bold text-white">{{ pendingCount }}</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-emerald-200 uppercase tracking-widest mb-1">Done</p>
              <p class="text-sm font-bold text-white">{{ doneCount }}</p>
            </div>
          </div>
        </div>

        <!-- Search card pulls up over hero -->
        <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 px-4 pt-3 pb-2">
          <!-- Search input -->
          <div class="relative mb-2">
            <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
            </svg>
            <input v-model="search" type="text" placeholder="Search by name, business or phone..."
              class="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white placeholder-slate-400 rounded-2xl pl-10 pr-10 py-3 text-sm
                     focus:outline-none focus:ring-2 focus:ring-rose-500 shadow-sm transition" />
            <button v-if="search" @click="search = ''"
              class="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Flash -->
      <div v-if="flash.notice" class="mx-4 md:mx-0 mb-2 px-4 py-3 rounded-xl bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 text-sm font-medium">
        {{ flash.notice }}
      </div>
      <div v-if="flash.alert" class="mx-4 md:mx-0 mb-2 px-4 py-3 rounded-xl bg-rose-50 dark:bg-rose-950 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-sm font-medium">
        {{ flash.alert }}
      </div>

      <!-- Empty state -->
      <div v-if="filteredContacts.length === 0" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 py-16 text-center">
        <svg class="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"/>
        </svg>
        <p class="text-sm font-medium text-slate-400">{{ search ? 'No requests match your search.' : 'No requests yet.' }}</p>
      </div>

      <!-- Desktop Table -->
      <div v-else>
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-visible">
          <div class="overflow-x-auto">
            <table class="w-full text-sm whitespace-nowrap">
              <thead>
                <tr class="border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                  <th class="text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-5 py-3.5">#</th>
                  <th @click="sortBy('full_name')" class="text-left text-[10px] font-semibold uppercase tracking-wide px-4 py-3.5 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200" :class="sortCol === 'full_name' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                    <span class="inline-flex items-center gap-1">Full Name <SortIcon :active="sortCol === 'full_name'" :asc="sortDir === 'asc'" /></span>
                  </th>
                  <th @click="sortBy('business_name')" class="text-left text-[10px] font-semibold uppercase tracking-wide px-4 py-3.5 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200" :class="sortCol === 'business_name' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                    <span class="inline-flex items-center gap-1">Business <SortIcon :active="sortCol === 'business_name'" :asc="sortDir === 'asc'" /></span>
                  </th>
                  <th @click="sortBy('phone_number')" class="text-left text-[10px] font-semibold uppercase tracking-wide px-4 py-3.5 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200" :class="sortCol === 'phone_number' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                    <span class="inline-flex items-center gap-1">Phone <SortIcon :active="sortCol === 'phone_number'" :asc="sortDir === 'asc'" /></span>
                  </th>
                  <th class="text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5">Address</th>
                  <th @click="sortBy('status')" class="text-left text-[10px] font-semibold uppercase tracking-wide px-4 py-3.5 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200" :class="sortCol === 'status' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                    <span class="inline-flex items-center gap-1">Status <SortIcon :active="sortCol === 'status'" :asc="sortDir === 'asc'" /></span>
                  </th>
                  <th class="text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5 min-w-[180px]">Admin Notes</th>
                  <th @click="sortBy('created_at')" class="text-left text-[10px] font-semibold uppercase tracking-wide px-4 py-3.5 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200" :class="sortCol === 'created_at' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                    <span class="inline-flex items-center gap-1">Submitted <SortIcon :active="sortCol === 'created_at'" :asc="sortDir === 'asc'" /></span>
                  </th>
                  <th class="text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-4 py-3.5"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(contact, idx) in displayedContacts" :key="contact.id"
                  class="border-b border-slate-50 dark:border-slate-800/50 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition">

                  <!-- # -->
                  <td class="px-5 py-3.5 text-xs text-slate-400 font-medium">{{ idx + 1 }}</td>

                  <!-- Full Name -->
                  <td class="px-4 py-3.5">
                    <div class="flex items-center gap-3">
                      <div class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                        :class="contact.status === 'done' ? 'bg-emerald-500' : 'bg-indigo-500'">
                        {{ (contact.full_name || '?').charAt(0).toUpperCase() }}
                      </div>
                      <p class="text-sm font-semibold text-slate-800 dark:text-white">{{ contact.full_name }}</p>
                    </div>
                  </td>

                  <!-- Business Name -->
                  <td class="px-4 py-3.5">
                    <p class="text-sm text-slate-700 dark:text-slate-200 font-medium">{{ contact.business_name }}</p>
                  </td>

                  <!-- Phone -->
                  <td class="px-4 py-3.5">
                    <a :href="`tel:${contact.phone_number}`"
                      class="text-sm text-indigo-600 dark:text-indigo-400 font-medium hover:underline">
                      {{ contact.phone_number }}
                    </a>
                  </td>

                  <!-- Address -->
                  <td class="px-4 py-3.5 max-w-[180px]">
                    <p class="text-xs text-slate-500 dark:text-slate-400 truncate" :title="contact.address">{{ contact.address }}</p>
                  </td>

                  <!-- Status (inline toggle) -->
                  <td class="px-4 py-3.5">
                    <button @click="toggleStatus(contact)"
                      :disabled="saving[contact.id]"
                      class="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full transition disabled:opacity-60"
                      :class="contact.status === 'done'
                        ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900'
                        : 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900'">
                      <span class="w-1.5 h-1.5 rounded-full"
                        :class="contact.status === 'done' ? 'bg-emerald-400' : 'bg-amber-400'"></span>
                      {{ contact.status === 'done' ? 'Done' : 'Pending' }}
                    </button>
                  </td>

                  <!-- Admin Notes (inline edit) -->
                  <td class="px-4 py-3.5 min-w-[200px]">
                    <div v-if="editingNotes[contact.id]" class="flex items-start gap-1.5">
                      <textarea v-model="notesDraft[contact.id]" rows="2"
                        class="flex-1 bg-white dark:bg-slate-800 border border-indigo-300 dark:border-indigo-600 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Add notes..."></textarea>
                      <div class="flex flex-col gap-1">
                        <button @click="saveNotes(contact)" :disabled="saving[contact.id]"
                          class="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 transition disabled:opacity-60">
                          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                          </svg>
                        </button>
                        <button @click="cancelNotes(contact)"
                          class="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 transition">
                          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div v-else @click="startEditNotes(contact)"
                      class="group flex items-start gap-1.5 cursor-pointer min-h-[32px] rounded-lg px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 transition">
                      <p class="text-xs text-slate-500 dark:text-slate-400 flex-1 whitespace-normal break-words">
                        {{ contact.admin_notes || '' }}
                      </p>
                      <svg class="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 shrink-0 mt-0.5 transition" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"/>
                      </svg>
                    </div>
                  </td>

                  <!-- Submitted At -->
                  <td class="px-4 py-3.5 text-xs text-slate-500 dark:text-slate-400">
                    <span :title="formatDate(contact.created_at)">{{ timeAgo(contact.created_at) }}</span>
                  </td>

                  <!-- WhatsApp -->
                  <td class="px-4 py-3.5">
                    <a :href="`https://wa.me/${contact.phone_number.replace(/\D/g, '')}`" target="_blank"
                      class="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900 transition">
                      <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </a>
                  </td>

                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Mobile Cards -->
        <div class="md:hidden space-y-2.5">
          <div v-for="(contact, idx) in displayedContacts" :key="contact.id"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden">

            <!-- Card Header -->
            <div class="p-4 border-b border-slate-100 dark:border-slate-800">
              <div class="flex items-start justify-between gap-3 mb-3">
                <div class="flex items-center gap-3 flex-1 min-w-0">
                  <div class="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                    :class="contact.status === 'done' ? 'bg-emerald-500' : 'bg-indigo-500'">
                    {{ (contact.full_name || '?').charAt(0).toUpperCase() }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-bold text-slate-800 dark:text-white truncate">{{ contact.full_name }}</p>
                    <p class="text-xs text-slate-500 dark:text-slate-400 truncate">{{ contact.business_name }}</p>
                  </div>
                </div>
                <button @click="toggleStatus(contact)"
                  :disabled="saving[contact.id]"
                  class="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full transition disabled:opacity-60 shrink-0"
                  :class="contact.status === 'done'
                    ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
                    : 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400'">
                  <span class="w-1.5 h-1.5 rounded-full"
                    :class="contact.status === 'done' ? 'bg-emerald-400' : 'bg-amber-400'"></span>
                  {{ contact.status === 'done' ? 'Done' : 'Pending' }}
                </button>
              </div>

              <!-- Contact Info -->
              <div class="space-y-2">
                <div class="flex items-center gap-2 text-xs">
                  <svg class="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"/>
                  </svg>
                  <a :href="`tel:${contact.phone_number}`" class="text-indigo-600 dark:text-indigo-400 font-medium hover:underline">
                    {{ contact.phone_number }}
                  </a>
                </div>
                <div class="flex items-start gap-2 text-xs">
                  <svg class="w-4 h-4 text-slate-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"/>
                  </svg>
                  <span class="text-slate-600 dark:text-slate-300 flex-1">{{ contact.address || '—' }}</span>
                </div>
              </div>
            </div>

            <!-- Admin Notes -->
            <div class="p-4 bg-slate-50 dark:bg-slate-800/50">
              <div class="flex items-start gap-2 mb-2">
                <svg class="w-4 h-4 text-slate-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/>
                </svg>
                <span class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Admin Notes</span>
              </div>

              <div v-if="editingNotes[contact.id]" class="space-y-2">
                <textarea v-model="notesDraft[contact.id]" rows="3"
                  class="w-full bg-white dark:bg-slate-800 border border-indigo-300 dark:border-indigo-600 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Add notes..."></textarea>
                <div class="flex items-center gap-2">
                  <button @click="saveNotes(contact)" :disabled="saving[contact.id]"
                    class="flex-1 py-2 px-3 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition disabled:opacity-60">
                    Save
                  </button>
                  <button @click="cancelNotes(contact)"
                    class="flex-1 py-2 px-3 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-300 dark:hover:bg-slate-600 transition">
                    Cancel
                  </button>
                </div>
              </div>
              <div v-else @click="startEditNotes(contact)"
                class="group cursor-pointer rounded-lg px-3 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-600 transition">
                <p class="text-sm text-slate-600 dark:text-slate-300 whitespace-normal break-words min-h-[20px]">
                  {{ contact.admin_notes || 'Tap to add notes...' }}
                </p>
              </div>
            </div>

            <!-- Card Footer -->
            <div class="p-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <span class="text-xs text-slate-500 dark:text-slate-400" :title="formatDate(contact.created_at)">
                {{ timeAgo(contact.created_at) }}
              </span>
              <a :href="`https://wa.me/${contact.phone_number.replace(/\D/g, '')}`" target="_blank"
                class="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900 transition">
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </a>
            </div>

          </div>
        </div>
      </div>

    </div>
  </AppLayout>
</template>

<script setup>
import { ref, computed, reactive, onMounted } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import SortIcon from '@/components/SortIcon.vue'

const props = defineProps({
  contacts: { type: Array, default: () => [] },
})

const page = usePage()
const flash = computed(() => page.props.flash || {})
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')

onMounted(() => { document.title = `Contact Submissions | ${businessName.value}` })

const search  = ref('')
const sortCol = ref('created_at')
const sortDir = ref('desc')

// local mutable copy so UI updates instantly
const localContacts = ref(props.contacts.map(c => ({ ...c })))

const pendingCount = computed(() => localContacts.value.filter(c => c.status === 'pending').length)
const doneCount    = computed(() => localContacts.value.filter(c => c.status === 'done').length)

const filteredContacts = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return localContacts.value
  return localContacts.value.filter(c =>
    (c.full_name     || '').toLowerCase().includes(q) ||
    (c.business_name || '').toLowerCase().includes(q) ||
    (c.phone_number  || '').toLowerCase().includes(q) ||
    (c.address       || '').toLowerCase().includes(q)
  )
})

const displayedContacts = computed(() => {
  const col = sortCol.value
  const asc = sortDir.value === 'asc'
  return [...filteredContacts.value].sort((a, b) => {
    let av = a[col] ?? ''
    let bv = b[col] ?? ''
    if (col === 'created_at') {
      av = av ? new Date(av).getTime() : 0
      bv = bv ? new Date(bv).getTime() : 0
    } else {
      av = String(av).toLowerCase()
      bv = String(bv).toLowerCase()
    }
    if (av < bv) return asc ? -1 : 1
    if (av > bv) return asc ? 1 : -1
    return 0
  })
})

function sortBy(col) {
  if (sortCol.value === col) {
    sortDir.value = sortDir.value === 'desc' ? 'asc' : 'desc'
  } else {
    sortCol.value = col
    sortDir.value = 'desc'
  }
}

// ── Saving state ──────────────────────────────────────────────────────────────
const saving = reactive({})

function patch(contact, data) {
  saving[contact.id] = true
  // optimistic update
  const local = localContacts.value.find(c => c.id === contact.id)
  if (local) Object.assign(local, data)

  router.patch(`/admin/contact_us_form_users/${contact.id}`, { user_contact: data }, {
    preserveScroll: true,
    onFinish: () => { saving[contact.id] = false },
  })
}

// ── Status toggle ─────────────────────────────────────────────────────────────
function toggleStatus(contact) {
  const newStatus = contact.status === 'done' ? 'pending' : 'done'
  patch(contact, { status: newStatus })
}

// ── Notes inline edit ─────────────────────────────────────────────────────────
const editingNotes = reactive({})
const notesDraft   = reactive({})

function startEditNotes(contact) {
  notesDraft[contact.id]   = contact.admin_notes || ''
  editingNotes[contact.id] = true
}

function cancelNotes(contact) {
  editingNotes[contact.id] = false
}

function saveNotes(contact) {
  editingNotes[contact.id] = false
  patch(contact, { admin_notes: notesDraft[contact.id] })
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatDate(dateStr) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function timeAgo(dateStr) {
  if (!dateStr) return '—'
  const seconds = Math.floor((Date.now() - new Date(dateStr)) / 1000)
  if (seconds < 60)            return 'Just now'
  if (seconds < 3600)          return Math.floor(seconds / 60) + 'm ago'
  if (seconds < 86400)         return Math.floor(seconds / 3600) + 'h ago'
  if (seconds < 86400 * 30)    return Math.floor(seconds / 86400) + 'd ago'
  if (seconds < 86400 * 365)   return Math.floor(seconds / (86400 * 30)) + 'mo ago'
  return Math.floor(seconds / (86400 * 365)) + 'y ago'
}
</script>
